import { useEffect, useRef, useState } from "react";
import { HostStripeMessageSchema, stripeBridgeEvent } from "@froppio/host-auth";
import { authService } from "@/core/auth/services/authService";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";

/** Opens a validated Stripe Checkout or Customer Portal URL: same tab on the web, system browser in the native app. */
export function useOpenBillingUrl(userId: string) {
  const captureOperation = useWorkspaceOperation();
  const pending = useRef<{ id: string; isCurrent: () => boolean; timeout?: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      const current = pending.current;
      if (!event.persisted || authService.kind === "host" || !current?.isCurrent()) return;
      window.clearTimeout(current.timeout);
      pending.current = null;
      setBusy(false);
      setMessage("Returned from Stripe. Your plan below comes from Froppio, not from the browser.");
    };
    const receive = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const parsed = HostStripeMessageSchema.safeParse(event.detail);
      if (!parsed.success || !("id" in parsed.data) || parsed.data.id !== pending.current?.id || !pending.current.isCurrent()) return;
      window.clearTimeout(pending.current.timeout);
      pending.current = null;
      setBusy(false);
      setMessage(parsed.data.type === "opened"
        ? "Continue in your browser. Return to Froppio to see your updated plan."
        : "Couldn't open your browser. Please try again.");
    };
    window.addEventListener(stripeBridgeEvent, receive);
    window.addEventListener("pageshow", restored);
    return () => {
      window.removeEventListener(stripeBridgeEvent, receive);
      window.removeEventListener("pageshow", restored);
      window.clearTimeout(pending.current?.timeout);
      pending.current = null;
    };
  }, [userId]);

  const open = async (createUrl: () => Promise<string>) => {
    if (pending.current) return;
    const isCurrent = captureOperation();
    const current = { id: crypto.randomUUID(), isCurrent, timeout: undefined as number | undefined };
    pending.current = current;
    setBusy(true);
    setMessage("");
    try {
      const url = await createUrl();
      if (pending.current !== current || !isCurrent()) return;
      if (authService.kind === "host") {
        if (!window.ReactNativeWebView) throw new Error("Native bridge unavailable.");
        current.timeout = window.setTimeout(() => {
          if (pending.current !== current || !isCurrent()) return;
          pending.current = null;
          setBusy(false);
          setMessage("Your app could not open Stripe. Update Froppio or try again.");
        }, 15_000);
        window.ReactNativeWebView.postMessage(JSON.stringify({ channel: stripeBridgeEvent, version: 1, type: "openBilling", id: current.id, userId, url }));
      } else {
        if (window.self !== window.top) throw new Error("Billing cannot open in a frame.");
        window.location.assign(url);
      }
    } catch {
      if (pending.current !== current || !isCurrent()) return;
      window.clearTimeout(current.timeout);
      pending.current = null;
      setBusy(false);
      setMessage("Couldn't open Stripe. Refresh your plan and try again.");
    }
  };
  return { open, busy, message };
}
